import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import { Helmet } from 'react-helmet-async';
import { articlePath } from '../lib/articles';
import type { PublicPageData } from '../lib/publicPage';

const ARTICLES_PER_PAGE = 6;

function BlogContent({ initialData }: { initialData?: PublicPageData | null }) {
  const { language, t, articles, loading } = useLanguage();
  const filteredArticles = articles.filter(article => article.language === language);
  const [visibleCount, setVisibleCount] = React.useState(initialData ? filteredArticles.length : ARTICLES_PER_PAGE);
  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMore = visibleCount < filteredArticles.length;

  React.useEffect(() => {
    setVisibleCount(initialData ? filteredArticles.length : ARTICLES_PER_PAGE);
  }, [language, initialData, filteredArticles.length]);

  React.useEffect(() => {
    document.body.setAttribute('data-lang', language);
    document.documentElement.setAttribute('lang', language === 'bg' ? 'bg' : 'en');
  }, [language]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{language === 'bg' ? 'Блог | Адвокат Петър Николов | Правни статии и анализи' : 'Blog | Attorney Petar Nikolov | Legal Insights & Analysis'}</title>
        <meta name="description" content={language === 'bg' ? 'Правни статии, анализи и новини от Адвокатска кантора Петър Николов. Семейно право, защита на потребители, административно право.' : 'Legal articles, analysis and insights from Petar Nikolov Law Firm. Family law, consumer protection, administrative law.'} />
        <link rel="canonical" href="https://advokatnikolov.com/blog" />
      </Helmet>
      <NavBar />

      <section className="pt-32 pb-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gold/10 rounded-full px-4 py-2 mb-6">
              <span className="w-2 h-2 bg-gold rounded-full"></span>
              <span className="text-gold font-medium text-sm">{t('blog.badge')}</span>
            </div>
            <h2 className="section-title text-center">{t('blog.title')}</h2>
            <p className="text-xl text-slate max-w-3xl mx-auto">
              {t('blog.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {loading ? (
              <div className="col-span-3 text-center text-slate">{t('blog.loading')}</div>
            ) : filteredArticles.length === 0 ? (
              <div className="col-span-3 text-center text-slate">{t('blog.notFound')}</div>
            ) : (
              visibleArticles.map((article) => {
                const counterpartLanguage = article.language === 'bg' ? 'en' : article.language === 'en' ? 'bg' : undefined;
                const counterpart = article.created_at && counterpartLanguage
                  ? articles.find(candidate => candidate.published && candidate.created_at === article.created_at && candidate.language === counterpartLanguage)
                  : undefined;
                return (
                  <article key={article.id} className="group bg-white rounded-2xl shadow-lg overflow-hidden card-hover h-full flex flex-col">
                    <div className="p-6 flex flex-col flex-grow">
                      <div className="flex items-center space-x-2 mb-4">
                        <span className="text-gold text-sm font-medium">{article.date}</span>
                      </div>
                      <a href={articlePath(article.slug)} className="text-xl font-serif text-navy mb-3 group-hover:text-gold transition-colors block hover:text-gold">
                        {article.title}
                      </a>
                      <p className="text-slate mb-4 leading-relaxed flex-grow">
                        {article.description}
                      </p>
                      {counterpart && <a href={articlePath(counterpart.slug)} aria-label={counterpart.language === 'en' ? 'English version' : 'Българска версия'} className="mt-3 inline-flex min-h-[44px] items-center text-sm font-semibold text-[#8a651a] underline decoration-gold/40 underline-offset-4 hover:text-navy">{counterpart.language === 'en' ? 'English version' : 'Българска версия'}</a>}
                      <a href={articlePath(article.slug)} className="text-gold font-semibold hover:text-gold/80 transition-colors mt-auto">
                        {t('blog.readArticle')}
                      </a>
                    </div>
                  </article>
                );
              })
            )}
          </div>

          {hasMore && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisibleCount(prev => prev + ARTICLES_PER_PAGE)}
                className="px-8 py-3 bg-navy text-white rounded-lg font-medium hover:bg-navy/90 transition-colors"
              >
                {language === 'bg' ? 'Зареди още статии' : 'Load more articles'}
              </button>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default BlogContent;
